import { Box, Divider, Paper, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { map } from 'rxjs';

import { HistoryResponse } from 'app/model/history-response';
import { SeamRequest } from 'app/model/seam-request';
import { seamRequestHistoryService } from 'app/services/seam-request/seam-request-history.service';
interface Props {
    request: SeamRequest;
}
interface HistoryItem {
    user: string;
    date: string;
    changes: Array<{
        field: string;
        oldVal: string;
        newVal: string;
    }>;
}
const DIVIDER_MARGIN = 2;
export const SeamRequestHistory: React.FC<Props> = ({ request }) => {
    const [items, setItems] = useState<HistoryItem[]>([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        const sub = seamRequestHistoryService
            .listAll({}, request)
            .pipe(
                map((responses: HistoryResponse[]) =>
                    responses
                        .map((r): HistoryItem | undefined => {
                            const user = r.userCreated?.username || '';
                            const dateStr = new Date(r.date).toLocaleString();
                            const allChanges = r.changes.map((c) => ({
                                field: c.field,
                                oldVal: c.oldValue?.userValue ?? '',
                                newVal: c.newValue?.userValue ?? '',
                            }));
                            if (0 === allChanges.length) {
                                return undefined;
                            }

                            return {
                                user,
                                date: dateStr,
                                changes: allChanges,
                            };
                        })
                        .filter((item): item is HistoryItem => !!item),
                ),
            )
            .subscribe((result) => {
                setItems(result);
                setLoading(false);
            });

        return () => {
            sub.unsubscribe();
        };
    }, [request]);
    if (loading) {
        return (
            <Box p={2} textAlign='center'>
                <Typography variant='body1'>Loading history...</Typography>
            </Box>
        );
    }

    return (
        <Box p={2}>
            <Paper variant='outlined' sx={{ p: 2 }}>
                {items.map((item, idx) => (
                    <React.Fragment key={idx}>
                        <Typography variant='body1' sx={{ mb: 2, mt: 0 < idx ? DIVIDER_MARGIN : 0 }}>
                            <Box component='span' sx={{ fontWeight: 500 }}>
                                {item.user}
                            </Box>
                            {' made changes - '}
                            <Box component='span' sx={{ color: 'text.secondary' }}>
                                {item.date}
                            </Box>
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: '200px 1fr 1fr',
                                columnGap: 2,
                                rowGap: 1,
                                mb: 2,
                            }}
                        >
                            {item.changes.map((change, cIdx) => (
                                <React.Fragment key={cIdx}>
                                    <Box>
                                        <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                                            {change.field}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant='body1' sx={{ fontWeight: 500, mr: 1 }} component='span'>
                                            Original:
                                        </Typography>
                                        <Typography variant='body1' component='span'>
                                            {change.oldVal}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant='body1' sx={{ fontWeight: 500, mr: 1 }} component='span'>
                                            New:
                                        </Typography>
                                        <Typography variant='body1' component='span'>
                                            {change.newVal}
                                        </Typography>
                                    </Box>
                                </React.Fragment>
                            ))}
                        </Box>
                        {idx < items.length - 1 && <Divider sx={{ my: DIVIDER_MARGIN }} />}
                    </React.Fragment>
                ))}
            </Paper>
        </Box>
    );
};
